/* 4youFit · capa de movimiento: intro de primera visita, entradas por scroll, títulos cinéticos,
   parallax del banner, inclinación 3D de tarjetas, botones magnéticos y marquesina sensible
   al scroll. Vanilla y sin dependencias.
   Todo lo que sigue al cursor o al scroll usa muelles (mantienen la velocidad si se
   interrumpen) y escribe `transform`/`translate` directamente en el elemento. */
(function () {
  'use strict';

  if (window.fyMovimientoListo) return;
  window.fyMovimientoListo = true;

  const html = document.documentElement;
  const reducido = window.matchMedia('(prefers-reduced-motion: reduce)');
  const punteroFino = window.matchMedia('(hover: hover) and (pointer: fine)');

  function enEditor() {
    return html.classList.contains('shopify-design-mode') || Boolean(window.Shopify && window.Shopify.designMode);
  }

  function efectosDeCursor() {
    return !reducido.matches && punteroFino.matches;
  }

  // ------------------------------------------------------------------ muelles y bucle
  // Integración semi-implícita: estable con dt de hasta 1/30 s.
  function muelle(rigidez, amortiguacion) {
    let valor = 0;
    let velocidad = 0;
    let objetivo = 0;
    return {
      get valor() { return valor; },
      get objetivo() { return objetivo; },
      set objetivo(v) { objetivo = v; },
      saltar(v) { valor = v; objetivo = v; velocidad = 0; },
      paso(dt) {
        velocidad += (-rigidez * (valor - objetivo) - amortiguacion * velocidad) * dt;
        valor += velocidad * dt;
        const enMarcha = Math.abs(valor - objetivo) > 0.001 || Math.abs(velocidad) > 0.001;
        if (!enMarcha) valor = objetivo;
        return enMarcha;
      },
    };
  }

  const activos = new Set();
  let rafId = 0;
  let ultimoTiempo = 0;

  function bucle(t) {
    const dt = ultimoTiempo ? Math.min((t - ultimoTiempo) / 1000, 1 / 30) : 1 / 60;
    ultimoTiempo = t;
    activos.forEach(function (fn) {
      if (!fn(dt)) activos.delete(fn);
    });
    if (activos.size) {
      rafId = requestAnimationFrame(bucle);
    } else {
      rafId = 0;
      ultimoTiempo = 0;
    }
  }

  function animar(fn) {
    activos.add(fn);
    if (!rafId) rafId = requestAnimationFrame(bucle);
  }

  // --------------------------------------------------------- entradas al hacer scroll
  let observador = null;

  function observarEntrada(el) {
    if (reducido.matches || enEditor() || !('IntersectionObserver' in window)) {
      el.classList.add('is-visible');
      return;
    }
    if (!observador) {
      observador = new IntersectionObserver(function (entradas) {
        entradas.forEach(function (entrada) {
          if (!entrada.isIntersecting) return;
          entrada.target.classList.add('is-visible');
          observador.unobserve(entrada.target);
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });
    }
    observador.observe(el);
  }

  // ---------------------------------------------------- títulos que emergen por palabras
  function escapar(texto) {
    return texto.replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function dividirTitulo(titulo) {
    if (titulo.querySelector('*:not(br)')) return; // con marcado propio: no se toca
    const lineas = titulo.innerHTML
      .split(/<br\s*\/?>/i)
      .map(function (trozo) {
        const temporal = document.createElement('div');
        temporal.innerHTML = trozo;
        return temporal.textContent.replace(/\s+/g, ' ').trim();
      })
      .filter(Boolean);
    if (!lineas.length) return;

    let indice = 0;
    titulo.setAttribute('aria-label', lineas.join(' '));
    titulo.innerHTML = lineas.map(function (linea) {
      const palabras = linea.split(' ').map(function (palabra) {
        return '<span class="fy-cinetico__mascara"><span class="fy-cinetico__in" style="--i:' + (indice++) + '">' +
          escapar(palabra) + '</span></span>';
      });
      return '<span class="fy-cinetico__linea" aria-hidden="true">' + palabras.join(' ') + '</span>';
    }).join('');
    titulo.classList.add('fy-dividir');
    observarEntrada(titulo);
  }

  // --------------------------------------------------------------- intro con el sello
  function initIntro() {
    const intro = document.querySelector('[data-fy-intro]');
    if (!intro) return;

    function terminar() {
      intro.remove();
      html.classList.remove('fy-con-intro');
      window.removeEventListener('keydown', saltar);
    }

    function saltar() {
      intro.classList.add('is-saltar');
      setTimeout(terminar, 40);
    }

    intro.addEventListener('animationend', function (e) {
      if (e.animationName === 'fy-intro-telon') terminar();
    });
    intro.addEventListener('click', saltar);
    window.addEventListener('keydown', saltar);
    setTimeout(terminar, 2400); // red de seguridad
  }

  // ------------------------------------------------------------- parallax del banner
  function initParallax(banner) {
    const capas = Array.from(banner.querySelectorAll('[data-fy-profundidad]'));
    if (!capas.length || !efectosDeCursor()) return;

    const x = muelle(80, 16);
    const y = muelle(80, 16);
    let visible = true;
    new IntersectionObserver(function (e) { visible = e[0].isIntersecting; }).observe(banner);

    function paso(dt) {
      const a = x.paso(dt);
      const b = y.paso(dt);
      capas.forEach(function (capa) {
        const p = parseFloat(capa.dataset.fyProfundidad) || 0;
        capa.style.transform = 'translate3d(' + (x.valor * p * 42).toFixed(2) + 'px, ' + (y.valor * p * 30).toFixed(2) + 'px, 0)';
      });
      return a || b;
    }

    banner.addEventListener('pointermove', function (e) {
      if (!visible || e.pointerType !== 'mouse') return;
      const r = banner.getBoundingClientRect();
      x.objetivo = ((e.clientX - r.left) / r.width - 0.5) * 2;
      y.objetivo = ((e.clientY - r.top) / r.height - 0.5) * 2;
      animar(paso);
    });
    banner.addEventListener('pointerleave', function () {
      x.objetivo = 0;
      y.objetivo = 0;
      animar(paso);
    });
  }

  // ------------------------------------------------ inclinación 3D con brillo de cursor
  function initInclinacion(el, maximo) {
    if (!efectosDeCursor()) return;

    const rx = muelle(210, 20);
    const ry = muelle(210, 20);
    const luz = muelle(160, 22);
    const brillo = document.createElement('span');
    brillo.className = 'fy-brillo-cursor';
    brillo.setAttribute('aria-hidden', 'true');
    el.appendChild(brillo);
    let bx = 0;
    let by = 0;

    function paso(dt) {
      const a = rx.paso(dt);
      const b = ry.paso(dt);
      const c = luz.paso(dt);
      const enMarcha = a || b || c;
      if (!enMarcha && rx.valor === 0 && ry.valor === 0) {
        el.style.transform = '';
        el.classList.remove('is-inclinando');
      } else {
        el.style.transform = 'perspective(900px) rotateX(' + rx.valor.toFixed(2) + 'deg) rotateY(' + ry.valor.toFixed(2) + 'deg)';
      }
      brillo.style.opacity = luz.valor.toFixed(3);
      brillo.style.transform = 'translate(' + bx.toFixed(1) + '%, ' + by.toFixed(1) + '%)';
      return enMarcha;
    }

    el.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      el.classList.add('is-inclinando');
      luz.objetivo = 1;
      animar(paso);
    });
    el.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      const r = el.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width;
      const ny = (e.clientY - r.top) / r.height;
      ry.objetivo = (nx - 0.5) * 2 * maximo;
      rx.objetivo = -(ny - 0.5) * 2 * maximo;
      bx = (nx - 0.5) * 50; // el brillo mide el doble que la tarjeta
      by = (ny - 0.5) * 50;
      animar(paso);
    });
    el.addEventListener('pointerleave', function () {
      rx.objetivo = 0;
      ry.objetivo = 0;
      luz.objetivo = 0;
      animar(paso);
    });
  }

  // ------------------------------------------------------------- botones magnéticos
  // Usa la propiedad `translate`, así el `scale` de :active (CSS) se compone sin pisarse.
  function initMagnetico(boton) {
    if (!efectosDeCursor()) return;
    const x = muelle(260, 20);
    const y = muelle(260, 20);

    function paso(dt) {
      const a = x.paso(dt);
      const b = y.paso(dt);
      boton.style.translate = x.valor.toFixed(2) + 'px ' + y.valor.toFixed(2) + 'px';
      return a || b;
    }

    boton.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      const r = boton.getBoundingClientRect();
      x.objetivo = (e.clientX - (r.left + r.width / 2)) * 0.22;
      y.objetivo = (e.clientY - (r.top + r.height / 2)) * 0.32;
      animar(paso);
    });
    boton.addEventListener('pointerleave', function () {
      x.objetivo = 0;
      y.objetivo = 0;
      animar(paso);
    });
  }

  // ------------------------------------------------- marquesina que responde al scroll
  function initMarquesina(seccion) {
    if (reducido.matches || seccion.dataset.fyScroll !== 'true') return;
    const pista = seccion.querySelector('.fy-marquesina__pista');
    const capa = seccion.querySelector('[data-fy-inclinacion]');
    if (!pista || !capa) return;

    const animacion = pista.getAnimations ? pista.getAnimations()[0] : null;
    const inclinacion = muelle(140, 20);
    const ritmo = muelle(90, 18);
    ritmo.saltar(1);
    let ritmoAplicado = 1;
    let visible = false;
    let ultimoY = window.scrollY;
    let ultimoT = performance.now();
    let reposo = 0;

    new IntersectionObserver(function (e) { visible = e[0].isIntersecting; }).observe(seccion);

    function paso(dt) {
      const a = inclinacion.paso(dt);
      const b = ritmo.paso(dt);
      capa.style.transform = 'skewX(' + inclinacion.valor.toFixed(2) + 'deg)';
      if (animacion && animacion.updatePlaybackRate && Math.abs(ritmo.valor - ritmoAplicado) > 0.02) {
        ritmoAplicado = ritmo.valor;
        animacion.updatePlaybackRate(ritmoAplicado);
      }
      return a || b;
    }

    window.addEventListener('scroll', function () {
      const ahora = performance.now();
      const velocidad = (window.scrollY - ultimoY) / Math.max(ahora - ultimoT, 8); // px/ms
      ultimoY = window.scrollY;
      ultimoT = ahora;
      if (!visible) return;
      inclinacion.objetivo = Math.max(-9, Math.min(9, velocidad * -7));
      ritmo.objetivo = 1 + Math.min(Math.abs(velocidad) * 3, 5);
      animar(paso);
      clearTimeout(reposo);
      reposo = setTimeout(function () {
        inclinacion.objetivo = 0;
        ritmo.objetivo = 1;
        animar(paso);
      }, 120);
    }, { passive: true });
  }

  // --------------------------------------------------------------------- arranque
  function una(el, clave) {
    if (el.dataset[clave]) return false;
    el.dataset[clave] = 'true';
    return true;
  }

  function initDentro(contenedor) {
    contenedor.querySelectorAll('[data-fy-entrar]').forEach(function (el) {
      if (una(el, 'fyEntrarListo')) observarEntrada(el);
    });
    contenedor.querySelectorAll('.fy-seccion:not(.fy-banner) h2.fy-titulo').forEach(function (titulo) {
      if (!titulo.closest('.fy-reveal') && una(titulo, 'fyTituloListo')) dividirTitulo(titulo);
    });
    contenedor.querySelectorAll('[data-fy-parallax]').forEach(function (banner) {
      if (una(banner, 'fyParallaxListo')) initParallax(banner);
    });
    contenedor.querySelectorAll('[data-fy-inclinar]').forEach(function (el) {
      if (una(el, 'fyInclinarListo')) initInclinacion(el, parseFloat(el.dataset.fyInclinar) || 7);
    });
    contenedor.querySelectorAll('[data-fy-magnetico]').forEach(function (boton) {
      if (una(boton, 'fyMagneticoListo')) initMagnetico(boton);
    });
    contenedor.querySelectorAll('[data-fy-marquesina]').forEach(function (seccion) {
      if (una(seccion, 'fyMarquesinaLista')) initMarquesina(seccion);
    });
  }

  function arrancar() {
    initIntro();
    initDentro(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }

  // Editor de temas: una sección recargada se prepara de nuevo (sin repetir la intro)
  document.addEventListener('shopify:section:load', function (e) { initDentro(e.target); });
})();
